/**
 * Self-hosted auth client (OCI / PostgreSQL JWT) — replaces Firebase Auth.
 *
 * The backend issues an HS256 JWT from POST /api/auth/login. We store it in
 * localStorage and attach it as a Bearer token on every request. No Google /
 * Firebase dependency.
 */
import { API_BASE } from './api';

const TOKEN_KEY = 'titan_token';

export interface LocalUser {
  uid: string;
  email: string;
  role: string;
  displayName?: string;
}

/** Decode a JWT payload (no verification — display only). */
function decodeJwt(token: string): Record<string, any> | null {
  try {
    const part = token.split('.')[1];
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

export function getStoredToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

export function clearToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/** True when a non-expired token is present. */
export function isTokenValid(): boolean {
  const t = getStoredToken();
  if (!t) return false;
  const claims = decodeJwt(t);
  if (!claims || !claims.exp) return false;
  return claims.exp * 1000 > Date.now();
}

/** Current user derived from the stored token, or null. */
export function currentLocalUser(): LocalUser | null {
  const t = getStoredToken();
  if (!t) return null;
  const c = decodeJwt(t);
  if (!c) return null;
  return {
    uid: c.uid || c.sub || '',
    email: c.email || '',
    role: c.role || 'Admin',
    displayName: c.displayName || (c.email ? String(c.email).split('@')[0] : ''),
  };
}

export async function login(email: string, password: string): Promise<LocalUser> {
  const resp = await fetch(`${API_BASE}/api/auth/login`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  });
  if (!resp.ok) {
    let detail = 'Login failed';
    try {
      const body = await resp.json();
      detail = body.detail || body.error?.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const data = await resp.json();
  if (!data.token) throw new Error('No token returned');
  setToken(data.token);
  return data.user as LocalUser;
}

export async function register(body: {
  email: string;
  password: string;
  displayName?: string;
  invitation_code?: string;
}): Promise<LocalUser> {
  const resp = await fetch(`${API_BASE}/api/auth/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!resp.ok) {
    let detail = 'Registration failed';
    try {
      const b = await resp.json();
      detail = b.detail || b.error?.message || detail;
    } catch {
      /* ignore */
    }
    throw new Error(detail);
  }
  const data = await resp.json();
  if (data.token) setToken(data.token);
  return data.user as LocalUser;
}

export function logout(): void {
  clearToken();
}
